import { NextPage } from 'next';
import { useTranslation } from 'react-i18next';
import { Store } from 'redux';
import { LogicMiddleware } from 'redux-logic';
import Head from 'next/head';

import { RootState, wrapper } from 'store/index';
import { get as getJobTypeDetail } from 'modules/job-types/service';
import { checkAndRedirectIfNeeded } from 'utils/marketing-pages';
import Layout from 'modules/layout';
import { setupPageAndLoadTranslations } from 'utils/page';
import InternalBlogPage from 'modules/marketing-pages/components/InternalBlogPage';
import { getBlogStructuredData } from 'modules/marketing-pages/components/InternalBlogPage/InternalBlogPage';
import {
  getInternalBlogPostByPostName,
  getInternalBlogPostsByJobTypeId,
} from 'modules/marketing-pages/service';
import { getCrumbs } from 'modules/common/components/Breadcrumbs';

import { setNewRequestModalProperties } from 'modules/requests/duck';

import type { VerticalPageJobType } from 'modules/job-types/types';
import type { Crumb } from 'modules/common/common.types';
import type {
  BlogPost,
  InternalBlogPost,
  SeoObject,
} from 'modules/marketing-pages/marketing-page.types';
import { isDefined } from 'utils/type-guards';
import { generateUrlWithoutUnwantedParameters } from 'src/utils/url';

interface Props {
  crumbs: Array<Crumb & { isVertical?: boolean }>;
  internalBlogPost: InternalBlogPost;
  jobType: VerticalPageJobType;
  relatedInternalBlogPosts: Array<BlogPost>;
  seo: SeoObject;
}

const InternalBlogPageRoot: NextPage<Props> = ({
  crumbs,
  internalBlogPost,
  jobType,
  relatedInternalBlogPosts,
  seo,
}) => {
  const {
    i18n: { language },
  } = useTranslation();
  const { title } = internalBlogPost;

  return (
    <Layout language={language} seo={seo} title={title}>
      <Head>
        <script
          type="application/ld+json"
          id="blog-structured-data"
          dangerouslySetInnerHTML={{
            __html: getBlogStructuredData(internalBlogPost),
          }}
        />
      </Head>
      <InternalBlogPage
        blogPost={internalBlogPost}
        crumbs={crumbs}
        jobType={jobType}
        relatedPosts={relatedInternalBlogPosts}
      />
    </Layout>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ defaultLocale, locale, resolvedUrl, query }) => {
      const { dispatch, logicMiddleware } = store as Store<RootState> & {
        logicMiddleware: LogicMiddleware;
      };

      if (resolvedUrl) {
        const reductedResolvedUrl = generateUrlWithoutUnwantedParameters(resolvedUrl);
        if (reductedResolvedUrl !== resolvedUrl) {
          // The permanent redirection here is done to prevent google search console from flagging page as duplicate
          return {
            redirect: {
              destination: reductedResolvedUrl,
              permanent: true,
            },
          };
        }
      }

      const { blogPostName, category1, category2, category3, jobTypeId } = query as {
        blogPostName: string;
        category1: string;
        category2: string;
        category3?: string;
        jobTypeId: string;
      };

      const categories = [category1, category2, category3].filter(isDefined);
      const redirectionResult = await checkAndRedirectIfNeeded<Props>({
        categories,
        dispatch,
        logicMiddleware,
        store,
        url: resolvedUrl,
      });

      if (redirectionResult) {
        return redirectionResult;
      }
      if (!blogPostName) {
        return { notFound: true };
      }

      const { currentLocale, i18nData } = await setupPageAndLoadTranslations({
        defaultLocale,
        locale,
        namespaces: ['common', 'marketingPages', 'internalBlog', 'form', 'request'],
        // @ts-expect-error Extended store
        store,
      });
      const language = currentLocale as 'ja' | 'en';
      // blog post names that are not url safe are encoded
      // additionally non url safe components cause ISE when the api is called.
      const encodedBlogPostName = encodeURIComponent(blogPostName);
      const internalBlogPost = await getInternalBlogPostByPostName({
        blogPostName: encodedBlogPostName,
      });
      if ('notFound' in internalBlogPost) {
        return { notFound: true };
      }

      const relatedInternalBlogPosts = await getInternalBlogPostsByJobTypeId({
        jobTypeId,
        // wordpress blog post slugs are lowercase and can be encoded. When encoded, even the encodings
        // are lowercase, so follow this convention in order to filter out the same blog post.
        options: { blogPostNameToFilter: encodedBlogPostName.toLowerCase() },
      });

      // Using format vertical-page for the jobCategories property on the jobType so that we can
      // generate user facing category crumbs
      const jobType = (await getJobTypeDetail(
        jobTypeId,
        language,
        'vertical-page'
      )) as VerticalPageJobType;
      const crumbs = getCrumbs({
        blogPostName: encodedBlogPostName,
        blogTitle: internalBlogPost.title,
        jobType,
        language,
      });
      // setNewRequestModalProperties is required to open the request modal
      dispatch(setNewRequestModalProperties({ jobTypeId }));

      const seo: SeoObject = {
        canonicalUrl: `/${categories.join('/')}/${jobTypeId}/blog/${encodedBlogPostName}`,
        description: internalBlogPost.metaDescription
          ? internalBlogPost.metaDescription
          : internalBlogPost.excerpt,
        ogDescription: internalBlogPost.metaDescription
          ? internalBlogPost.metaDescription
          : internalBlogPost.excerpt,
        ogTitle: internalBlogPost.title,
        showTwitterLargeThumbnail: true,
      };

      if (internalBlogPost.featuredImageUrl) {
        seo.ogImage = internalBlogPost.featuredImageUrl;
      }

      return {
        props: {
          ...i18nData,
          crumbs,
          internalBlogPost,
          jobType,
          relatedInternalBlogPosts,
          seo,
        },
      };
    }
);

export default InternalBlogPageRoot;
